'use client'

import {
  SEO_REPORT_CLICK_SOURCE,
  toBuyLinkHref,
} from '~/src/lib/withRedirectClickSource'
import { repairImageKitUrl } from '@dupe/common/constants'

import { useEffect, useState } from 'react'
import { BadgeCheck, CircleMinus, ImageOff, Sparkle, X } from 'lucide-react'
import Image from 'next/image'
import type { CanonicalReportProduct } from '@dupe/common/research-report'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '~/src/components/ui/drawer'
import { cn } from '~/src/lib/utils'
import type { SeoReportAnalyticsCallbacks } from './SeoReportAnalyticsContext'
import { SeoReportProductQuotes } from './SeoReportProductQuotes'
import { formatSpecValue, isRenderableSpecValue } from './seoReportSpecFormat'
import type { SeoRenderableBuyLink } from '~/src/hooks/useSeoReportRetailerLinks'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SeoReportProductDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  product: CanonicalReportProduct | null
  /** Enriched buy links (with logos / prices / verified flag). Falls back to
   *  the snapshot buyLinks on the product when not provided. */
  buyLinks?: SeoRenderableBuyLink[]
  onBuyLinkClick?: SeoReportAnalyticsCallbacks['onProductClick']
  analyticsSection?: string
  analyticsPosition?: number
  communityQuotes?: Array<{
    text: string
    source?: string
    url?: string | null
    platform?: 'tiktok' | 'instagram' | 'reddit' | 'youtube' | 'community'
  }>
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function canUseNextImage(url: string): boolean {
  if (url.startsWith('/')) return true
  try {
    const hostname = new URL(url).hostname
    return hostname === 'ik.imagekit.io' || hostname === 'assets.dupe.com'
  } catch {
    return false
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function ProductImage({
  src,
  alt,
  className,
}: {
  src: string
  alt: string
  className?: string
}) {
  const [hasError, setHasError] = useState(false)
  const imgSrc = repairImageKitUrl(src)

  if (hasError) {
    return (
      <div className='absolute inset-0 flex items-center justify-center bg-accent'>
        <ImageOff className='size-8 text-foreground-muted' strokeWidth={1.5} />
      </div>
    )
  }

  if (canUseNextImage(imgSrc)) {
    return (
      <Image
        src={imgSrc}
        alt={alt}
        fill
        sizes='(max-width: 768px) 100vw, 400px'
        className={cn('object-contain', className)}
        onError={() => setHasError(true)}
      />
    )
  }
  return (
    <img
      src={imgSrc}
      alt={alt}
      className={cn('absolute inset-0 h-full w-full object-contain', className)}
      loading='lazy'
      onError={() => setHasError(true)}
    />
  )
}

function Section({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className='flex flex-col gap-3'>
      <h3 className='text-lg font-semibold text-foreground'>{title}</h3>
      {children}
    </div>
  )
}

function KeySpecsList({ specs }: { specs: Record<string, unknown> }) {
  const entries = Object.entries(specs).filter(([, value]) =>
    isRenderableSpecValue(value)
  )
  if (entries.length === 0) return null

  return (
    <div className='flex flex-col gap-3'>
      <h3 className='text-base font-semibold text-foreground'>Key specs</h3>
      <table className='w-full border-collapse'>
        <tbody>
          {entries.map(([key, val]) => {
            const display = formatSpecValue(val)
            return (
              <tr key={key} className='border-b border-accent align-top'>
                <td className='w-2/5 py-2 pr-3 text-sm text-foreground-muted'>
                  {key
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (c) => c.toUpperCase())}
                </td>
                <td
                  className='py-2 text-right text-sm font-medium text-foreground-secondary'
                  title={display}
                >
                  {display}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function formatPrice(
  price: number | null | undefined,
  currency?: string | null
): string {
  if (price == null) return ''
  const roundedPrice = Math.round(price)
  const currencyCode = currency ?? 'USD'
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(roundedPrice)
  } catch {
    return `$${roundedPrice}`
  }
}

function RetailerRow({
  link,
  currency,
  onClick,
}: {
  link: SeoRenderableBuyLink
  currency?: string | null
  onClick?: () => void
}) {
  const [logoError, setLogoError] = useState(false)
  const showLogo = link.logoUrl && !logoError
  return (
    <a
      href={toBuyLinkHref(link.url, SEO_REPORT_CLICK_SOURCE)}
      target='_blank'
      rel='sponsored nofollow noopener noreferrer'
      className='flex items-center justify-between border-b border-black/[0.08] py-3 transition-opacity hover:opacity-80'
      onClick={onClick}
    >
      <div className='flex items-center gap-2'>
        {showLogo ? (
          <span className='flex h-7 w-7 items-center justify-center overflow-hidden rounded border border-border bg-white'>
            <img
              src={link.logoUrl ?? undefined}
              alt={`${link.label} logo`}
              loading='lazy'
              className='h-full w-full object-cover'
              onError={() => setLogoError(true)}
            />
          </span>
        ) : (
          <span className='flex h-7 w-7 items-center justify-center rounded border border-border bg-white text-xs font-bold text-foreground-secondary'>
            {link.label.charAt(0).toUpperCase()}
          </span>
        )}
        <span className='flex items-center gap-1'>
          <span className='text-sm font-semibold leading-5 text-info-700'>
            {link.label}
          </span>
          {link.verified && (
            <BadgeCheck
              className='h-3.5 w-3.5 shrink-0 text-info-700'
              aria-label='Verified seller'
            />
          )}
        </span>
      </div>
      {link.price != null && (
        <span className='text-sm font-semibold leading-5 text-info-700'>
          {formatPrice(link.price, currency)}
        </span>
      )}
    </a>
  )
}

export function SeoReportProductDrawer({
  open,
  onOpenChange,
  product,
  buyLinks,
  onBuyLinkClick,
  analyticsSection = 'product-drawer',
  analyticsPosition = 0,
  communityQuotes,
}: SeoReportProductDrawerProps) {
  const [selectedImageIdx, setSelectedImageIdx] = useState(0)

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIdx(0)
  }, [product?.slug])

  if (!product) return null

  const heroImage =
    product.images[selectedImageIdx] ?? product.images[0] ?? null
  // Prefer the enriched buyLinks (with logos/prices) when supplied; fall back
  // to the snapshot retailer links on the product itself.
  const renderableBuyLinks: SeoRenderableBuyLink[] =
    buyLinks && buyLinks.length > 0
      ? buyLinks
      : (product.retailerLinks?.buyLinks ?? []).map((link) => ({
          label: link.label,
          url: link.url,
          price: link.price ?? null,
        }))
  const visibleCommunityQuotes =
    communityQuotes ??
    (product.ranking?.whyCommunity ?? []).slice(0, 3).map((text) => ({
      text,
      url: null,
    }))

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      direction='right'
      shouldScaleBackground={false}
    >
      <DrawerContent
        className='fixed inset-y-0 right-0 left-auto z-50 flex h-full w-full max-w-3xl flex-col rounded-none bg-white md:rounded-l-2xl'
        hideNotch
      >
        <div className='flex h-full flex-col overflow-y-auto'>
          {/* Header — sticky so the product name stays visible while scrolling */}
          <div className='sticky top-0 z-10 flex items-center justify-between gap-4 border-b border-border bg-white px-6 py-4'>
            <DrawerTitle className='min-w-0 flex-1 text-xl font-bold text-foreground'>
              {product.name}
            </DrawerTitle>
            <DrawerDescription className='sr-only'>
              Product details, pricing, and retailer links for {product.name}
            </DrawerDescription>
            <DrawerClose
              aria-label='Close'
              className='-mr-2 flex size-9 shrink-0 items-center justify-center rounded-full text-foreground-muted opacity-60 transition-opacity hover:opacity-100'
            >
              <X className='size-5' />
            </DrawerClose>
          </div>

          {/* Content */}
          <div className='flex flex-col gap-8 px-6 py-6'>
            {/* Hero image */}
            {heroImage && (
              <div className='relative aspect-square w-full overflow-hidden rounded-xl border border-border'>
                <ProductImage src={heroImage} alt={product.name} />
              </div>
            )}

            {/* Image gallery carousel — extends past the parent's px-6 so
                thumbnails can scroll edge-to-edge without getting clipped. */}
            {product.images.length > 1 && (
              <div
                data-vaul-no-drag
                className='-mx-6 flex snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth px-6 [scroll-padding-inline:24px] touch-pan-x scrollbar-none'
              >
                {product.images.map((img, i) => (
                  <button
                    key={`img-${i}`}
                    type='button'
                    onClick={() => setSelectedImageIdx(i)}
                    className={cn(
                      'relative h-24 w-24 flex-shrink-0 snap-start overflow-hidden rounded-lg border-2 transition-colors',
                      selectedImageIdx === i
                        ? 'border-sky-accent-500'
                        : 'border-border hover:border-foreground-muted'
                    )}
                  >
                    <ProductImage src={img} alt={`${product.name} ${i + 1}`} />
                  </button>
                ))}
              </div>
            )}

            {/* Summary */}
            {product.assessment?.summary && (
              <Section title='Summary'>
                <p className='text-base leading-7 text-foreground-secondary'>
                  {product.assessment.summary}
                </p>
              </Section>
            )}

            {/* Buy links — same row treatment as the card (logo + label +
                verified badge + price). */}
            {renderableBuyLinks.length > 0 && (
              <Section title='Where to buy'>
                <div className='flex flex-col'>
                  {renderableBuyLinks.map((link) => (
                    <RetailerRow
                      key={link.url}
                      link={link}
                      currency={product.pricing.currency}
                      onClick={
                        onBuyLinkClick
                          ? () =>
                              onBuyLinkClick({
                                productSlug: product.slug,
                                productName: product.name,
                                outboundUrl: link.url,
                                price: link.price ?? null,
                                position: analyticsPosition,
                                section: analyticsSection,
                              })
                          : undefined
                      }
                    />
                  ))}
                </div>
              </Section>
            )}

            {/* Key specs -- inline list. Long comma-separated values are
                clamped via formatSpecValue to keep rows from blowing up. */}
            {product.specs && Object.keys(product.specs).length > 0 && (
              <KeySpecsList specs={product.specs} />
            )}

            {/* Highlights */}
            {(product.assessment?.pros?.length ?? 0) > 0 && (
              <Section title='Highlights'>
                <ul className='flex flex-col gap-2'>
                  {product.assessment?.pros?.map((item, i) => (
                    <li key={`pro-${i}`} className='flex items-start gap-2'>
                      <Sparkle
                        className='mt-0.5 size-4 shrink-0 text-info-500'
                        fill='currentColor'
                        strokeWidth={0}
                      />
                      <span className='text-sm text-foreground-secondary'>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            {/* Worth knowing */}
            {(product.assessment?.cons?.length ?? 0) > 0 && (
              <Section title='Worth knowing'>
                <ul className='flex flex-col gap-2'>
                  {product.assessment?.cons?.map((item, i) => (
                    <li key={`con-${i}`} className='flex items-start gap-2'>
                      <CircleMinus className='mt-0.5 size-4 shrink-0 text-foreground-muted' />
                      <span className='text-sm text-foreground-secondary'>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <SeoReportProductQuotes quotes={visibleCommunityQuotes} />
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
